import React, { useEffect } from "react";
import { Row, Col, Input, Drawer, DatePicker, Select, Form, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import TextArea from "antd/lib/input/TextArea";
import CancelArrow from "../../../../assets/images/cancelArrow.svg";
import { DRAWER_MASK } from "../../Core/modals/customerDrawerUi";
import "./v4Modals.css";
import useTransactionStore from "../../../../stores/useTransactionStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import { isCreditSaleModificationBlocked, CREDIT_SALE_MODIFICATION_BLOCKED_MESSAGE } from "../../../../utils/creditSaleModificationGuard";

// Why the cashier is giving the discount. Sent with the form values as
// discountReason; the discount itself is worked out exactly as before.
export const DISCOUNT_REASONS = ["Loyal customer", "Price match", "Damaged or soiled piece", "Staff purchase", "Manager approved", "Other"];

// "Under 10% without a manager." from the rule's approval bands: a value at or
// above the lowest band asks for approval (useCartOperations.applyManualDiscount).
export const approvalNote = (rule, currSymbol = "") => {
  const bands = rule?.enableApproval === "Y" ? (rule.mPricingApprovals || []).filter((a) => Number.isFinite(Number(a?.min))) : [];
  if (bands.length === 0) return null;
  const lowest = bands.reduce((a, b) => (Number(b.min) < Number(a.min) ? b : a));
  const min = Number(lowest.min);
  if (min <= 0) return "A manager approves this discount.";
  const limit = lowest.type === "V" ? `${currSymbol}${min.toLocaleString("en-IN")}` : `${min}%`;
  return `Under ${limit} without a manager.`;
};

const ManualDiscountDrawer = ({
  manualDiscountModalVisible,
  setManualDiscountModalVisible,
  manualDiscountForm,
  manualDiscountInput,
  setManualDiscountInput,
  manualDiscountTypes,
  handleManualDiscountKeyPress,
  applyManualDiscount,
  selectedManualDiscountType,
  setSelectedManualDiscountTypeValue,
  enableManualDiscountInput,
  removeAllDiscounts,
  selectedProductInCart,
  setSelectedProductInCart,
  cart,
  tillData,
  totalBillDiscountData,
  discountAdditionalInfo,
  setDiscountAdditionalInfo,
  discountAdditionalInfoForm,
  setDiscountVerifictionData,
  discountInforApplication,
  handleDiscountTypeChange,
  valueInputRef,
  searchInputRef,
}) => {
  const currSymbol = tillData?.tillAccess?.csBunit?.currencies?.[0]?.currSymbol || "";
  const precision = tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision || 2;
  const hasAdditionalInfo = discountAdditionalInfo?.length > 0;
  const hasProduct = selectedProductInCart && Object.keys(selectedProductInCart).length > 0;
  const isLineLevel = selectedManualDiscountType && (selectedManualDiscountType.type === "FD" || selectedManualDiscountType.type === "PD");
  const cartItems = (cart?.items || []).filter((i) => i.isCanceled !== "Y");

  // When the drawer (re)opens, check if the selected cart item (or the cart
  // itself for total-level discounts) already has a manual discount applied.
  // If so, prepopulate the form with the existing discount type and value so
  // the cashier sees what was previously applied instead of a blank slate.
  // Only reset to blank when no discount exists. (BUG-1098)
  useEffect(() => {
    if (manualDiscountModalVisible) {
      // Check for existing line-level discount on the selected product.
      // Total-level (TD) discounts also stamp mPricingruleId onto every discounted
      // item (see CheckoutTotalManualDiscount.js), so the rule type must be
      // restricted to FD/PD here — otherwise a TD discount is misread as line-level
      // and its value is looked up in the wrong array (lineLevelDiscount instead of
      // totalLevelDiscount), leaving the value blank while the type still matches.
      const existingLineDiscount = hasProduct && selectedProductInCart?.mPricingruleId
        ? manualDiscountTypes.find(
            (rule) => rule.mPricingrulesId === selectedProductInCart.mPricingruleId && (rule.type === "FD" || rule.type === "PD")
          )
        : null;

      // Check for existing total-level discount on the cart
      const existingTotalDiscount = !existingLineDiscount && cart?.manualDiscountData?.totalLevelDiscount?.length > 0
        ? (() => {
            const lastTotalDiscount = cart.manualDiscountData.totalLevelDiscount[cart.manualDiscountData.totalLevelDiscount.length - 1];
            const matchedRule = manualDiscountTypes.find((rule) => rule.mPricingrulesId === lastTotalDiscount?.pricingRule);
            return matchedRule ? { rule: matchedRule, discountValue: lastTotalDiscount.discountValue } : null;
          })()
        : null;

      // Check for existing line-level discount via manualDiscountData (when product has no mPricingruleId but data exists)
      const existingLineFromData = !existingLineDiscount && !existingTotalDiscount && hasProduct
        ? (() => {
            const lineDiscounts = cart?.manualDiscountData?.lineLevelDiscount || [];
            const match = lineDiscounts.find((d) => d.lineId === selectedProductInCart.lineId);
            if (!match) return null;
            const matchedRule = manualDiscountTypes.find((rule) => rule.mPricingrulesId === match.pricingRule);
            return matchedRule ? { rule: matchedRule, discountValue: match.discountValue } : null;
          })()
        : null;

      if (existingLineDiscount) {
        // Prepopulate with the line-level discount applied to the selected item
        const lineDiscountData = (cart?.manualDiscountData?.lineLevelDiscount || []).find(
          (d) => d.lineId === selectedProductInCart.lineId
        );
        const discountValue = lineDiscountData?.discountValue ?? (
          existingLineDiscount.discountType === "V"
            ? (existingLineDiscount.amountDiscount ?? "")
            : (existingLineDiscount.percentageDiscount ?? "")
        );
        setSelectedManualDiscountTypeValue(existingLineDiscount);
        setManualDiscountInput(String(discountValue || ""));
        manualDiscountForm.setFieldsValue({
          discountName: existingLineDiscount.mPricingrulesId,
          discountValue: String(discountValue || ""),
        });
      } else if (existingTotalDiscount) {
        // Prepopulate with the total-level discount
        setSelectedManualDiscountTypeValue(existingTotalDiscount.rule);
        setManualDiscountInput(String(existingTotalDiscount.discountValue || ""));
        manualDiscountForm.setFieldsValue({
          discountName: existingTotalDiscount.rule.mPricingrulesId,
          discountValue: String(existingTotalDiscount.discountValue || ""),
        });
      } else if (existingLineFromData) {
        // Prepopulate from manualDiscountData line-level entry
        setSelectedManualDiscountTypeValue(existingLineFromData.rule);
        setManualDiscountInput(String(existingLineFromData.discountValue || ""));
        manualDiscountForm.setFieldsValue({
          discountName: existingLineFromData.rule.mPricingrulesId,
          discountValue: String(existingLineFromData.discountValue || ""),
        });
      } else {
        // No existing discount — reset to blank state, including any stale
        // additional-info state left over from a previously removed discount (BUG-1794)
        manualDiscountForm.resetFields();
        setManualDiscountInput("");
        setSelectedManualDiscountTypeValue(null);
        discountAdditionalInfoForm.resetFields();
        setDiscountAdditionalInfo([]);
        setDiscountVerifictionData?.(null);
        // Only one discount set up: pick it so the value box is ready. This
        // only selects the type; nothing is applied until Apply is pressed.
        if (manualDiscountTypes.length === 1) handleSelectType(manualDiscountTypes[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualDiscountModalVisible, manualDiscountTypes]);

  const handleClose = () => {
    manualDiscountForm.resetFields();
    discountAdditionalInfoForm.resetFields();
    setManualDiscountInput("");
    setSelectedManualDiscountTypeValue(null);
    setManualDiscountModalVisible(false);
    setDiscountAdditionalInfo([]);
    setDiscountVerifictionData?.(null);
    useTransactionStore.getState().setDiscountAppliedData(null);
    searchInputRef?.current?.focus();
  };

  const handleSelectType = (rule) => {
    handleDiscountTypeChange(rule.mPricingrulesId);
    manualDiscountForm.setFieldsValue({ discountName: rule.mPricingrulesId });
  };

  const handleSelectCartItem = (item) => {
    if (setSelectedProductInCart) {
      setSelectedProductInCart(item);
    }
  };

  const press = (key) => (e) => {
    e.preventDefault();
    handleManualDiscountKeyPress(key);
  };

  const getDiscountPreview = () => {
    if (!selectedManualDiscountType || !manualDiscountInput || Number(manualDiscountInput) <= 0) return null;
    if (selectedManualDiscountType.type === "TD") {
      if (!totalBillDiscountData) return null;
      return {
        discount: totalBillDiscountData.discountedAmount?.toFixed(precision),
        afterDiscount: totalBillDiscountData.finalAmount?.toFixed(precision),
      };
    }
    if (hasProduct) {
      if (selectedManualDiscountType.discountType === "P") {
        // Mirrors CheckoutPercentageDiscount.js: the discount is taken off the
        // per-unit realPrice, then tax is recomputed on the reduced price — it is
        // NOT a straight subtraction from the pre-discount (tax-inclusive) nettotal.
        const realPrice = Number(selectedProductInCart.realPrice) || 0;
        const weight = Number(selectedProductInCart.weight) || 0;
        const taxRate = Number(selectedProductInCart.taxRate) || 0;
        const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
        const taxIncludeFlag =
          saleTypeData?.customer?.istaxincluded === "Y"
            ? "Y"
            : tillData?.tillAccess?.csBunit?.isTaxIncluded
              ? tillData.tillAccess.csBunit.isTaxIncluded
              : "Y";
        const discountAmtPerUnit = (Number(manualDiscountInput) / 100) * realPrice;
        const sp = realPrice - discountAmtPerUnit;
        const mrp = sp * weight;
        const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + taxRate / 100) : (mrp * taxRate) / 100;
        const afterDiscount = taxIncludeFlag === "Y" ? mrp : mrp + tax;
        return {
          discount: (discountAmtPerUnit * weight).toFixed(precision),
          afterDiscount: afterDiscount.toFixed(precision),
        };
      }
      // Fixed-amount (V) discount is taken off the tax-inclusive gross directly
      // (CheckoutFlatDiscount.js), so subtracting from nettotal matches it.
      const discountAmt = Number(manualDiscountInput);
      return {
        discount: discountAmt.toFixed(precision),
        afterDiscount: (selectedProductInCart.nettotal - discountAmt).toFixed(precision),
      };
    }
    return null;
  };

  const preview = getDiscountPreview();
  const needsProductSelection = isLineLevel && !hasProduct;
  // Before / off / after is always on screen; it fills in as the value is typed.
  const beforeRaw = isLineLevel ? (hasProduct ? selectedProductInCart.nettotal : null) : (totalBillDiscountData?.totalBillPrice ?? cart?.total);
  const before = beforeRaw === null || beforeRaw === undefined || beforeRaw === "" || !Number.isFinite(Number(beforeRaw)) ? null : Number(beforeRaw).toFixed(precision);
  const hasValue = Number(manualDiscountInput) > 0;
  const offLabel =
    selectedManualDiscountType && hasValue
      ? selectedManualDiscountType.discountType === "V"
        ? `${currSymbol}${manualDiscountInput} off`
        : `${manualDiscountInput}% off`
      : null;
  const whose = isLineLevel ? "Item" : "Bill";
  const note = approvalNote(selectedManualDiscountType, currSymbol);

  const canApply = !!selectedManualDiscountType && (!isLineLevel || hasProduct);
  const typeLabel = (rule) => (rule.type === "TD" ? "Whole bill" : rule.type === "FD" || rule.type === "PD" ? "One item" : rule.type || "");
  const valueLabel = (rule) =>
    rule.discountType === "V"
      ? rule.amountDiscount
        ? `${currSymbol}${rule.amountDiscount} fixed`
        : `${currSymbol} off`
      : rule.percentageDiscount
        ? `${rule.percentageDiscount}% fixed`
        : "% off";
  const keys = ["1", "2", "3", "x", "4", "5", "6", "clear", "7", "8", "9", "0", "00", "."];

  return (
    <Drawer
      placement="bottom"
      closable={false}
      open={manualDiscountModalVisible}
      className="filter v4m"
      styles={{ mask: DRAWER_MASK, body: { overflowX: "hidden", overflowY: "auto" } }}
      onClose={handleClose}
      height="73vh"
    >
      <div className="v4m-head">
        <div>
          <h2 className="v4m-title">Manual discount</h2>
          <p className="v4m-sub">
            {isLineLevel && hasProduct ? `For ${selectedProductInCart.name}` : "Pick a discount type, enter the value, then apply it"}
          </p>
        </div>
        <button type="button" className="v4m-ib" aria-label="close" onClick={handleClose}>
          <CloseOutlined />
        </button>
      </div>
      <Form form={manualDiscountForm} onFinish={applyManualDiscount}>
        <Form.Item name="discountName" hidden>
          <Input />
        </Form.Item>

        <Row gutter={[20, 0]}>
          {/* ── LEFT: value + keypad ── */}
          <Col span={hasAdditionalInfo ? 9 : 10}>
            <p className="v4m-label">
              {selectedManualDiscountType ? (
                <>
                  {selectedManualDiscountType.name}{" "}
                  <span className="v4m-chip">{selectedManualDiscountType.discountType === "V" ? "Amount" : "Percentage"}</span>
                </>
              ) : (
                "Value"
              )}
            </p>

            {/* Form.Item stores the typed value before the Input's onChange regex runs,
                so filter here too or a negative ("-5") reaches applyManualDiscount (BUG-2392).
                Fall back to "" (not undefined) so an empty field stays controlled and the
                rejected text is cleared from the box too. */}
            <Form.Item
              name="discountValue"
              style={{ marginBottom: 0 }}
              normalize={(val, prev) => (/^\d*\.?\d*$/.test(val ?? "") ? val : (prev ?? ""))}
            >
              <Input
                className="v4m-in big"
                disabled={!selectedManualDiscountType || enableManualDiscountInput}
                ref={valueInputRef}
                value={manualDiscountInput}
                inputMode="decimal"
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*\.?\d*$/.test(val)) {
                    setManualDiscountInput(val);
                    manualDiscountForm.setFieldsValue({ discountValue: val });
                  }
                }}
                onPressEnter={(e) => {
                  e.preventDefault();
                  manualDiscountForm.submit();
                }}
                style={{ textAlign: "right" }}
                suffix={<b>{selectedManualDiscountType?.discountType === "V" ? currSymbol : selectedManualDiscountType ? "%" : ""}</b>}
                placeholder={
                  !selectedManualDiscountType
                    ? "Select a discount type"
                    : selectedManualDiscountType.discountType === "V"
                      ? "Enter Amount"
                      : "Enter %"
                }
              />
            </Form.Item>
            {needsProductSelection && <p className="v4m-note warn">Pick the item this discount is for from the list on the right.</p>}

            <div className="v4m-pn v4m-sum" style={{ marginTop: 10 }} aria-label="Discount preview">
              <div>
                <span>{whose} before</span>
                <span>{before === null ? "—" : `${currSymbol}${before}`}</span>
              </div>
              <div className={preview ? "save" : undefined}>
                <span>{offLabel || "Discount"}</span>
                <span>{preview ? `−${currSymbol}${preview.discount}` : "—"}</span>
              </div>
              <div className="total">
                <span>{whose} after</span>
                <span>{preview ? `${currSymbol}${preview.afterDiscount}` : before === null ? "—" : `${currSymbol}${before}`}</span>
              </div>
            </div>

            <div className="v4m-reason">
              <label className="v4m-label" htmlFor="v4m-discount-reason">
                Reason
              </label>
              <Form.Item name="discountReason" style={{ marginBottom: 0 }}>
                <Select
                  id="v4m-discount-reason"
                  className="v4m-select"
                  placeholder="Pick a reason"
                  allowClear
                  options={DISCOUNT_REASONS.map((r) => ({ value: r, label: r }))}
                  getPopupContainer={(node) => node.parentElement || document.body}
                />
              </Form.Item>
              {note && <p className="v4m-note">{note}</p>}
            </div>

            <div className="v4m-keys">
              {keys.map((k) => (
                <button key={k} type="button" className="v4m-k" aria-label={k === "x" ? "Backspace" : undefined} onClick={press(k)}>
                  {k === "x" ? <img src={CancelArrow} alt="" width={18} height={11} /> : k === "clear" ? "Clear" : k}
                </button>
              ))}
              <button
                type="button"
                className={`v4m-bp v4m-k wide${canApply ? "" : " off"}`}
                style={{ height: 52 }}
                onClick={(e) => {
                  e.preventDefault();
                  if (needsProductSelection) {
                    message.warning("Please select a product from cart first");
                    return;
                  }
                  manualDiscountForm.submit();
                }}
                disabled={!selectedManualDiscountType}
              >
                {offLabel && !needsProductSelection ? `Apply ${offLabel}` : "Apply discount"}
              </button>
            </div>

          </Col>

          {/* ── RIGHT: discount types + cart items ── */}
          <Col span={hasAdditionalInfo ? 8 : 14} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Cart item picker — shown for line-level discounts */}
            {isLineLevel && cartItems.length > 0 && (
              <div>
                <p className="v4m-label">{hasProduct ? "Item" : "Pick the item"}</p>
                <div style={{ overflowY: "auto", maxHeight: hasProduct ? "none" : "12vh", display: "flex", flexDirection: "column", gap: 4 }}>
                  {(hasProduct ? [selectedProductInCart] : cartItems).map((item, idx) => {
                    const isActive = hasProduct && selectedProductInCart?.key === item.key;
                    return (
                      <button
                        type="button"
                        key={item.key ?? idx}
                        className={`v4m-line${isActive ? " on" : ""}`}
                        onClick={() => !hasProduct && handleSelectCartItem(item)}
                      >
                        <span>{item.name}</span>
                        <span>
                          {currSymbol}
                          {item.nettotal}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="v4m-label" style={{ marginBottom: -4 }}>
              Discount type
            </p>
            <div style={{ overflowY: "auto", maxHeight: isLineLevel ? (hasProduct ? "36vh" : "30vh") : "40vh", padding: 2 }}>
              {manualDiscountTypes.length === 0 ? (
                <p className="v4m-empty">No manual discounts are set up for this store.</p>
              ) : (
                <div className="v4m-tiles" role="listbox" aria-label="Discount types">
                  {manualDiscountTypes.map((rule) => {
                    const isSelected = selectedManualDiscountType?.mPricingrulesId === rule.mPricingrulesId;
                    return (
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        key={rule.mPricingrulesId}
                        className={`v4m-tile${isSelected ? " on" : ""}`}
                        onClick={() => handleSelectType(rule)}
                      >
                        <b>{rule.name}</b>
                        <span className="v4m-chips">
                          <span className="v4m-chip">{typeLabel(rule)}</span>
                          <span className="v4m-chip">{valueLabel(rule)}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="button"
              className="v4m-bs v4m-block"
              onClick={async () => {
                // BUG-2355: removing discounts rewrites a retrieved Credit Sale's totals.
                if (isCreditSaleModificationBlocked(cart, usePOSConfigStore.getState().posConfig)) {
                  message.info(CREDIT_SALE_MODIFICATION_BLOCKED_MESSAGE);
                  return;
                }
                manualDiscountForm.resetFields();
                setManualDiscountInput("");
                setSelectedManualDiscountTypeValue(null);
                // Clear stale additional-info state so it doesn't leak into the
                // next discount attempt (BUG-1794)
                discountAdditionalInfoForm.resetFields();
                setDiscountAdditionalInfo([]);
                setDiscountVerifictionData?.(null);
                useTransactionStore.getState().setDiscountAppliedData(null);
                await removeAllDiscounts(true, false, true);
              }}
            >
              Remove all discounts
            </button>
          </Col>

          {/* ── ADDITIONAL INFO ── */}
          {hasAdditionalInfo && (
            <Col span={7}>
              <p className="v4m-label">Additional information</p>
              <div className="v4m-pn" style={{ overflowY: "auto", maxHeight: "30vh" }}>
                <Form form={discountAdditionalInfoForm} layout="vertical" size="small">
                  {discountAdditionalInfo.map((field) => (
                    <Form.Item
                      key={field.fieldName}
                      name={field.fieldName}
                      label={
                        <span style={{ fontWeight: 600, color: "#3d4856", fontSize: 12 }}>
                          {field.mandatory === "Y" && <span style={{ color: "#a12a1f", marginRight: 3 }}>*</span>}
                          {field.displayLabel}
                        </span>
                      }
                      style={{ marginBottom: 8 }}
                    >
                      {field.fieldType === "DT" ? (
                        <DatePicker style={{ width: "100%", borderRadius: 8 }} format="DD-MM-YYYY" />
                      ) : field.fieldType === "DTE" ? (
                        <DatePicker showTime style={{ width: "100%", borderRadius: 8 }} />
                      ) : field.fieldType === "NU" ? (
                        <Input type="number" style={{ borderRadius: 8 }} />
                      ) : field.fieldType === "LI" ? (
                        <Select placeholder="Select..." className="custom-select" style={{ width: "100%", borderRadius: 8 }}>
                          {field?.validationRule?.split(",").map((item) => (
                            <Select.Option key={item} value={item}>
                              {item}
                            </Select.Option>
                          ))}
                        </Select>
                      ) : (
                        <TextArea rows={2} style={{ borderRadius: 8 }} />
                      )}
                    </Form.Item>
                  ))}
                </Form>
              </div>
              <button
                type="button"
                className="v4m-bp v4m-block"
                style={{ marginTop: 10 }}
                onClick={() => {
                  try {
                    const values = discountAdditionalInfoForm.getFieldsValue();
                    const mandatoryFields = discountAdditionalInfo?.filter((f) => f.mandatory === "Y") || [];
                    const missing = mandatoryFields.filter((f) => !values[f.fieldName] && values[f.fieldName] !== 0);
                    if (missing.length > 0) {
                      message.warning(`Please fill: ${missing.map((f) => f.displayLabel).join(", ")}`);
                      return;
                    }
                    discountInforApplication(values);
                  } catch (err) {
                    console.error("[ManualDiscount] Submit error:", err);
                    message.error("Submit failed: " + err.message);
                  }
                }}
              >
                Submit
              </button>
            </Col>
          )}
        </Row>
      </Form>
    </Drawer>
  );
};

export default ManualDiscountDrawer;
